import { z } from "zod/v4";
import type { QuoteDimension } from "./quantity-dimensions";

/** Closed sums of dimensional products. No model-authored expressions are evaluated. */
export const quantityTermSchema = z.object({
  sign: z.enum(["add", "subtract"]),
  operands: z.array(z.string().min(1).max(100)).min(1).max(3),
});
export type QuantityTerm = z.infer<typeof quantityTermSchema>;

export function quantityProducts(terms: QuantityTerm[] | undefined, operands: string[],
  dimensions: QuoteDimension[], outputUnit: string): number {
  if (!terms?.length || !terms.some(t => t.sign === "add"))
    throw new Error("A run calculation needs at least one included measurement");
  const referenced = new Set(terms.flatMap(t => t.operands));
  if (referenced.size !== operands.length || operands.some(id => !referenced.has(id)))
    throw new Error("Calculation terms must use exactly the quantity operands");
  const seen = new Set<string>();
  return terms.reduce((sum, term) => {
    if (!["add", "subtract"].includes(term.sign) || term.operands.length < 1 || term.operands.length > 3 ||
      new Set(term.operands).size !== term.operands.length)
      throw new Error("Invalid dimensional product");
    const key = [...term.operands].sort().join("|");
    if (seen.has(key)) throw new Error("A construction run or area cannot be counted twice");
    seen.add(key);
    const ds = term.operands.map(id => {
      const d = dimensions.find(d => d.id === id);
      if (!d) throw new Error("A quantity operand is missing");
      return d;
    });
    const productUnit = ds.length === 1 ? ds[0].unit :
      ds.every(d => d.unit === "m") ? ({ 2: "m²", 3: "m³" } as Record<number, string>)[ds.length] : "";
    if (productUnit !== outputUnit)
      throw new Error("Every construction run/area product must match the quote's quantity unit");
    if (term.sign === "subtract" && !ds.some(d => /^(?:Opening|Excluded)\b/i.test(d.label)))
      throw new Error("Deductions must identify the Opening or Excluded work");
    const product = ds.reduce((n, d) => n * d.value, 1);
    return sum + (term.sign === "subtract" ? -product : product);
  }, 0);
}

export function quantityProductsSummary(terms: QuantityTerm[] | undefined, dimensions: QuoteDimension[]) {
  return (terms ?? []).map((term, i) => {
    const text = term.operands.map(id => {
      const d = dimensions.find(d => d.id === id);
      return d ? `${d.label}: ${d.value} ${d.unit} (${d.source})` : "Missing measurement";
    }).join(" × ");
    return `${term.sign === "subtract" ? " − " : i ? " + " : ""}(${text})`;
  }).join("");
}
